<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import CodeField from '@js/components/fields/CodeField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { object, string } from 'yup';

const { document, documentGroups } = defineProps({
    document: {
        type: Object,
        required: false,
    },
    documentGroups: {
        type: Array,
        required: true,
    },
});

const { route } = useRoute();

const form = reactive({
    group: document
        ? documentGroups.find((group) => group.value === document.group.value)
        : (documentGroups[0] ?? null),
    name: document ? document.name : '',
    url: document ? document.url : '',
    svgIcon: document ? document.svgIcon : '',
});

const validationSchema = object({
    group: object().required('Ce champ est obligatoire.'),
    name: string().required('Ce champ est obligatoire.'),
    url: string()
        .url('Veuillez saisir une URL valide.')
        .required('Ce champ est obligatoire.'),
    svgIcon: string().nullable(),
});

const isSubmitting = ref(false);

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.postDocument', {
                ...(document ? { document: document.id } : {}),
            }),
            {
                documentGroupId: form.group.value,
                name: form.name,
                url: form.url,
                svgIcon: form.svgIcon,
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route('front.projectManagement.docAndFaq', {
                    tab: 'documents',
                }),
            );
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <FormRoot
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <FormColumn>
            <FormContent>
                <FormGrid :columns="2">
                    <ListboxField
                        v-model="form.group"
                        field-id="group"
                        field-name="group"
                        label="Groupe"
                        :options="documentGroups"
                        required
                    >
                        <template #buttonContent>
                            {{ form.group?.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>

                    <InputField
                        v-model="form.name"
                        label="Nom"
                        field-name="name"
                        field-id="name"
                        required
                    />
                </FormGrid>
            </FormContent>

            <FormContent>
                <FormGrid :columns="1">
                    <InputField
                        v-model="form.url"
                        label="URL"
                        field-name="url"
                        field-id="url"
                        type="url"
                        required
                    />
                </FormGrid>
            </FormContent>

            <FormContent>
                <FormGrid :columns="1">
                    <CodeField
                        v-model="form.svgIcon"
                        field-name="svgIcon"
                        field-id="svgIcon"
                        label="Icône SVG"
                        legend="Rechercher des icônes SVG sur <a href='https://icon-sets.iconify.design/' target='_blank'>https://icon-sets.iconify.design/</a>"
                        size="small"
                    />
                </FormGrid>
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="document"
                    :url="
                        route('front.projectManagement.deleteDocument', {
                            document: document.id,
                        })
                    "
                    :callback="
                        () => {
                            router.visit(
                                route('front.projectManagement.docAndFaq', {
                                    tab: 'documents',
                                }),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
